import { FormFieldConfig, ValidatorFn, ValidatorResult } from './types';

export class Validators {
  static required(message = 'Este campo es obligatorio'): ValidatorFn {
    return (value: any) => {
      if (value === null || value === undefined) return message;
      if (typeof value === 'string' && value.trim().length === 0) return message;
      if (Array.isArray(value) && value.length === 0) return message;
      return null;
    };
  }

  static minLength(min: number, message?: string): ValidatorFn {
    const defaultMsg = `Debe tener al menos ${min} caracteres`;
    return (value: any) => {
      if (!value) return null; // Let required validator handle empty value
      if (String(value).trim().length < min) return message || defaultMsg;
      return null;
    };
  }

  static maxLength(max: number, message?: string): ValidatorFn {
    const defaultMsg = `No puede exceder ${max} caracteres`;
    return (value: any) => {
      if (!value) return null;
      if (String(value).trim().length > max) return message || defaultMsg;
      return null;
    };
  }

  static number(message = 'Debe ser un número válido'): ValidatorFn {
    return (value: any) => {
      if (value === null || value === undefined || value === '') return null;
      if (isNaN(Number(value))) return message;
      return null;
    };
  }

  static email(message = 'Correo electrónico no válido'): ValidatorFn {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return (value: any) => {
      if (!value) return null;
      return re.test(String(value).trim().toLowerCase()) ? null : message;
    };
  }

  /**
   * Mexican RFC validation:
   * Accepts both Persona Física (13 chars) and Persona Moral (12 chars), with or without dashes.
   */
  static rfc(message = 'RFC no tiene un formato válido (12 o 13 caracteres)'): ValidatorFn {
    const cleanRe = /^([A-ZÑ&]{3,4}) ?(?:- ?)?(\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])) ?(?:- ?)?([A-Z\d]{2}[A\d])$/i;
    return (value: any) => {
      if (!value) return null;
      const str = String(value).trim().toUpperCase();
      return cleanRe.test(str) ? null : message;
    };
  }

  static rfcFisica(message = 'RFC de persona física no válido (formato: ABCD-YYMMDD-XXX)'): ValidatorFn {
    const rfcFisicaRe = /^[A-Za-z]{4}[-]{1}[0-9]{6}[-]{1}[0-9A-Za-z]{3}$/;
    const rfcFisicaClean = /^[A-Za-z]{4}[0-9]{6}[0-9A-Za-z]{3}$/;
    return (value: any) => {
      if (!value) return null;
      const str = String(value).trim();
      return rfcFisicaRe.test(str) || rfcFisicaClean.test(str) ? null : message;
    };
  }

  static rfcMoral(message = 'RFC de persona moral no válido (formato: ABC-YYMMDD-XXX)'): ValidatorFn {
    const rfcMoralRe = /^[A-Za-z]{3}[-]{1}[0-9]{6}[-]{1}[0-9A-Za-z]{3}$/;
    const rfcMoralClean = /^[A-Za-z]{3}[0-9]{6}[0-9A-Za-z]{3}$/;
    return (value: any) => {
      if (!value) return null;
      const str = String(value).trim();
      return rfcMoralRe.test(str) || rfcMoralClean.test(str) ? null : message;
    };
  }

  static pattern(regex: RegExp, message: string): ValidatorFn {
    return (value: any) => {
      if (!value) return null;
      return regex.test(String(value)) ? null : message;
    };
  }

  /**
   * Helper to create any custom validation function
   */
  static custom(
    fn: (value: any, allValues: Record<string, any>, field?: FormFieldConfig) => ValidatorResult
  ): ValidatorFn {
    return fn;
  }

  // Aliases for legacy DynamicForm compatibility
  static rfc_fisica = Validators.rfcFisica;
  static rfc_moral = Validators.rfcMoral;
}

/**
 * Runs a validator function and returns an error string or null
 */
export function evaluateValidator(
  validator: ValidatorFn,
  value: any,
  allValues: Record<string, any>,
  field?: FormFieldConfig
): string | null {
  try {
    const res = validator(value, allValues, field);
    if (!res || res === true) return null;
    if (typeof res === 'string') return res;
    if (typeof res === 'boolean' && !res) {
      return `El valor de ${field?.label || 'este campo'} no es válido`;
    }
    if (typeof res === 'object' && 'result' in res) {
      if (!res.result) {
        return `El campo no cumple la validación ${res.name || ''}`.trim();
      }
      return null;
    }
  } catch (err) {
    console.error('Error evaluating validator:', err);
    return 'Error de validación';
  }
  return null;
}
